/**
 * Da nuvem de itens para linhas e colunas.
 *
 * Duas medidas saíram da amostra e explicam as constantes daqui:
 *
 *  - **Tolerância de linha: 2 pt.** Na OS, a célula de "Tempo" é desenhada
 *    0,8 pt abaixo do resto da linha, e o "Plano" da tabela de materiais 0,8 pt
 *    acima. Com tolerância menor que isso a linha se parte em duas; com muito
 *    mais, linhas vizinhas (12 pt de distância) se fundem.
 *  - **Folga de coluna: 4 pt.** Números aparecem alinhados à direita e começam
 *    depois do x do cabeçalho ("Quebra" em 330, "5,88 %" em 339). A coluna vai
 *    do seu x menos a folga até o x da coluna seguinte menos a folga.
 */

import type { ItemDeTexto } from './pdf.js';

export const TOLERANCIA_LINHA = 2;
export const FOLGA_COLUNA = 4;

export interface Linha {
  y: number;
  itens: ItemDeTexto[];
}

/** Agrupa itens em linhas visuais, de cima para baixo. */
export function agruparEmLinhas(
  itens: readonly ItemDeTexto[],
  tolerancia = TOLERANCIA_LINHA,
): Linha[] {
  const ordenados = [...itens].sort((a, b) => a.y - b.y || a.x - b.x);
  const linhas: Linha[] = [];
  for (const item of ordenados) {
    const ultima = linhas[linhas.length - 1];
    if (ultima && Math.abs(item.y - ultima.y) <= tolerancia) {
      ultima.itens.push(item);
      // O y da linha é o do primeiro item; manter estável evita que uma
      // sequência de itens levemente descendentes arraste a linha inteira.
    } else {
      linhas.push({ y: item.y, itens: [item] });
    }
  }
  for (const l of linhas) l.itens.sort((a, b) => a.x - b.x);
  return linhas;
}

/** Só os itens dentro de uma faixa vertical. */
export function entre(itens: readonly ItemDeTexto[], deY: number, ateY: number): ItemDeTexto[] {
  return itens.filter((i) => i.y >= deY && i.y < ateY);
}

export interface Coluna {
  nome: string;
  x: number;
}

/**
 * As colunas de uma tabela, tiradas da própria linha de cabeçalho.
 *
 * Deliberadamente não há lista fixa de posições no código: se o sistema da
 * gráfica mudar a largura de uma coluna numa atualização, o cabeçalho muda
 * junto e a leitura continua certa.
 */
export function colunasDaLinha(linha: Linha): Coluna[] {
  return linha.itens.map((i) => ({ nome: i.texto, x: i.x }));
}

/** A que coluna um item pertence, pela posição. */
export function colunaDe(colunas: readonly Coluna[], x: number, folga = FOLGA_COLUNA): number {
  let indice = 0;
  for (let i = 0; i < colunas.length; i += 1) {
    const c = colunas[i];
    if (c && x >= c.x - folga) indice = i;
    else break;
  }
  return indice;
}

/** Uma linha de tabela já distribuída em células, uma por coluna. */
export type Celulas = string[];

/**
 * Distribui os itens de uma linha nas colunas. Itens que caem na mesma coluna
 * são juntados por espaço (acontece quando o texto tem tabulação interna).
 */
export function celulasDaLinha(linha: Linha, colunas: readonly Coluna[]): Celulas {
  const celulas: string[] = Array.from({ length: colunas.length }, () => '');
  for (const item of linha.itens) {
    const c = colunaDe(colunas, item.x);
    celulas[c] = celulas[c] ? `${celulas[c]} ${item.texto}` : item.texto;
  }
  return celulas;
}

/**
 * Junta as linhas de uma tabela em registros, tratando continuação de célula.
 *
 * Uma linha **sem nada na primeira coluna** é continuação da anterior: é assim
 * que "MULTILITH" e "NUMERADOR" (uma célula em duas linhas) voltam a ser uma
 * coisa só. Sem isso, "NUMERADOR" viraria uma etapa fantasma.
 */
export function registrosDaTabela(
  linhas: readonly Linha[],
  colunas: readonly Coluna[],
): Celulas[] {
  const registros: Celulas[] = [];
  for (const linha of linhas) {
    const celulas = celulasDaLinha(linha, colunas);
    const temNome = (celulas[0] ?? '').trim() !== '';
    const anterior = registros[registros.length - 1];
    if (!temNome && anterior) {
      for (let i = 0; i < celulas.length; i += 1) {
        const novo = celulas[i];
        if (!novo) continue;
        anterior[i] = anterior[i] ? `${anterior[i]} ${novo}` : novo;
      }
    } else {
      registros.push(celulas);
    }
  }
  return registros;
}

/** Texto de uma célula, ou null quando vazia. */
export function celula(celulas: Celulas, i: number): string | null {
  const v = (celulas[i] ?? '').trim();
  return v === '' ? null : v;
}

/** "1.095" → 1095 · "0,29" → 0.29 · "" → null. */
export function numeroBr(texto: string | null | undefined): number | null {
  if (!texto) return null;
  const limpo = texto.trim().replace(/\./g, '').replace(',', '.');
  if (!/^-?\d+(\.\d+)?$/.test(limpo)) return null;
  const n = Number(limpo);
  return Number.isFinite(n) ? n : null;
}

/** "1:02" → 3720 segundos. */
export function segundosDeHhmm(texto: string | null | undefined): number | null {
  if (!texto) return null;
  const m = /^(\d{1,3}):([0-5]\d)$/.exec(texto.trim());
  if (!m) return null;
  return Number(m[1]) * 3600 + Number(m[2]) * 60;
}
